import { useMutation, useQueryClient, QueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { gql } from "@apollo/client"
import { executeQuerySideload } from "../../helpers/studyPlan"
import { OrgRefScope } from "../../../../types/gqlTypes/graphql"

export interface AssignUnassignLicensesInput {
  orgRefScope: OrgRefScope
  assignStudentIds?: string[]
  unassignStudentIds?: string[]
}

export interface AssignUnassignLicensesByFiltersInput {
  operation: "assign" | "unassign"
  orgRefScope: OrgRefScope
  assignStudentIds?: string[]
  unassignStudentIds?: string[]
  useFilters: boolean
  filters?: {
    orgRefScope: OrgRefScope
    grade?: string | null
    searchTerm?: string | null
    hasActiveLicense?: boolean | null
    page: number
    pageSize: number
  }
}

export interface AssignUnassignLicensesResult {
  assignedStudentIds: string[]
  unassignedStudentIds: string[]
  invalidStudentIds: string[]
  orgId?: string | null
  orgType?: "DISTRICT" | "SCHOOL" | null
  orgName?: string | null
}

const ASSIGN_UNASSIGN_LICENSES_MUTATION = gql`
  mutation AssignUnassignLicenses($data: AssignUnassignLicensesInput!) {
    licensePool {
      assignUnassignLicenses(data: $data) {
        assignedStudentIds
        unassignedStudentIds
        invalidStudentIds
        orgId
        orgType
        orgName
      }
    }
  }
`

export async function assignUnassignLicenses(
  input: AssignUnassignLicensesInput
): Promise<AssignUnassignLicensesResult> {
  const response = await executeQuerySideload(
    ASSIGN_UNASSIGN_LICENSES_MUTATION,
    { data: input }
  )
  return response.licensePool.assignUnassignLicenses
}

const QUERY_GRAPHQL_DATA_KEY = "GRAPHQL-TABLE-DATA-KEY"

// Helper function to extract district ID from variables
function getDistrictId(
  variables: AssignUnassignLicensesInput | AssignUnassignLicensesByFiltersInput
): string | undefined {
  if ("filters" in variables && variables.filters?.orgRefScope?.districtId) {
    return variables.filters.orgRefScope.districtId
  }
  return variables.orgRefScope?.districtId
}

// Helper function to create mutation handlers
function createLicenseMutationHandlers(queryClient: QueryClient) {
  return {
    onSuccess: async (
      data: AssignUnassignLicensesResult,
      variables:
        | AssignUnassignLicensesInput
        | AssignUnassignLicensesByFiltersInput
    ) => {
      const {
        assignedStudentIds = [],
        unassignedStudentIds = [],
        invalidStudentIds = []
      } = data || {}

      const assignedCount = assignedStudentIds.length
      const unassignedCount = unassignedStudentIds.length
      const invalidCount = invalidStudentIds.length

      // Show success messages
      if (assignedCount > 0) {
        enqueueSnackbar(
          `Successfully assigned licenses to ${assignedCount} student${
            assignedCount !== 1 ? "s" : ""
          }.`,
          { variant: "success" }
        )
      }

      if (unassignedCount > 0) {
        enqueueSnackbar(
          `Successfully unassigned licenses from ${unassignedCount} student${
            unassignedCount !== 1 ? "s" : ""
          }.`,
          { variant: "success" }
        )
      }

      // Show warning for invalid students
      if (invalidCount > 0) {
        enqueueSnackbar(
          `Could not process ${invalidCount} student${
            invalidCount !== 1 ? "s" : ""
          }. Please check they belong to the selected district.`,
          { variant: "warning" }
        )
      }

      // Use org details from the response
      const districtId = getDistrictId(variables)

      // Invalidate student queries to refetch with updated license status
      await queryClient.invalidateQueries({
        queryKey: [QUERY_GRAPHQL_DATA_KEY],
        refetchType: "active",
        exact: false
      })

      // Invalidate ALL license statistics queries (not just for specific district)
      await queryClient.invalidateQueries({
        queryKey: ["license-statistics"],
        refetchType: "active",
        exact: false
      })

      // Invalidate widget queries to ensure all widgets update
      await queryClient.invalidateQueries({
        queryKey: ["widget"],
        refetchType: "active",
        exact: false
      })

      // As a fallback, invalidate ALL queries to ensure everything updates
      // This is more aggressive but ensures no query is missed
      await queryClient.invalidateQueries()

      // Dispatch success event
      window.dispatchEvent(new CustomEvent("bulkActionSuccess"))
    },
    onError: (error: Error) => {
      console.error("License assignment error:", error)
      const errorMessage =
        error?.message || "Failed to update license assignments."
      enqueueSnackbar(errorMessage, { variant: "error" })
    }
  }
}

const ASSIGN_UNASSIGN_LICENSES_BY_FILTERS_MUTATION = gql`
  mutation AssignUnassignLicensesByFilters(
    $data: AssignUnassignLicensesByFiltersInput!
  ) {
    licensePool {
      assignUnassignLicensesByFilters(data: $data) {
        assignedStudentIds
        unassignedStudentIds
        invalidStudentIds
        orgId
        orgType
        orgName
      }
    }
  }
`

export async function assignUnassignLicensesByFilters(
  input: AssignUnassignLicensesByFiltersInput
): Promise<AssignUnassignLicensesResult> {
  const response = await executeQuerySideload(
    ASSIGN_UNASSIGN_LICENSES_BY_FILTERS_MUTATION,
    { data: input }
  )
  return response.licensePool.assignUnassignLicensesByFilters
}

export function useAssignUnassignLicenses() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: assignUnassignLicenses,
    ...createLicenseMutationHandlers(queryClient)
  })
}

export function useAssignUnassignLicensesByFilters() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: assignUnassignLicensesByFilters,
    ...createLicenseMutationHandlers(queryClient)
  })
}
